import { useMemo, useState } from "react";
import { T, UI } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { REGION_HE, companyRegionDef, classifyRegionLabel } from "../constants/regions";
import { Card } from "./Card";
import { usePortfolioSegments, exposureSource } from "../utils/exposureData";
import { valuedHoldings, concentration, sectors, regions, gaps, SECTOR_PENDING } from "../utils/portfolioExposures";
import { SECTOR_UNKNOWN } from "../utils/sectorClassify";
import { dateNumeric, asOfNote } from "../utils/formatDate";
import Money, { Amount } from "./Money";
import { useFxRates } from "../utils/fxRates";
import { useIsDesktop } from "../utils/hooks";
import EmptyState from "./EmptyState";
import LTR from "./LTR";
import { Skeleton, Loading } from "./Skeleton";

// ─── חשיפות התיק ─────────────────────────────────────────────────────────────
// What sits behind the holdings, in five measurements of one total: the
// value of every holding with a price, summed exactly as the portfolio screen
// sums it (utils/portfolioExposures.js). Each section adds to 100% of that
// total, and the part we cannot place is a line of its own, never a gap in
// the arithmetic.
//
// It describes and stops. No score, no comparison to a norm, no "high" or
// "low". A share is a measured fact; what it means is the reader's call
// (VISION.md). Bars are neutral ink · green and red belong to a price moving,
// and a share of the portfolio is not one. One ring, for sectors, since that
// is the one set of identities; everything else is bars to compare.

const pct1 = v => `${v.toFixed(1)}%`;

const clock = sec => {
  const d = new Date(sec * 1000);
  return `${dateNumeric(d, { year: false })} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

function Section({ n, title, basis, source, children, style }) {
  return (
    <Card style={{ padding: "20px 20px 18px", ...style }}>
      <h2 style={{ ...TYPE.heading, margin: 0, display: "flex", alignItems: "baseline", gap: 8 }}>
        <LTR className="si-num" style={{ fontSize: 13, color: UI.text.muted, fontWeight: 700 }}>{n}</LTR>
        {title}
      </h2>
      {basis && <div style={{ ...TYPE.caption, marginTop: 6, fontSize: 12.5 }}>{basis}</div>}
      <div style={{ marginTop: T.scale[3] }}>{children}</div>
      {source && (
        <div style={{ ...TYPE.caption, marginTop: T.scale[3], paddingTop: T.scale[2], borderTop: `1px solid ${UI.line.hairline}` }}>
          {source}
        </div>
      )}
    </Card>
  );
}

// A share of the portfolio, drawn against the whole (100%): every section on
// this screen adds to 100, so the bar's length is a true share, not a ratio
// between rows.
function BarRow({ label, value, muted = false, sub }) {
  return (
    <div style={{ padding: `${T.scale[1] + 2}px 0` }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: T.space.sm }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: muted ? UI.text.secondary : UI.text.primary, minWidth: 0 }}>{label}</span>
        <LTR className="si-num" style={{ fontSize: 15, fontWeight: 700, color: muted ? UI.text.secondary : UI.text.primary, flexShrink: 0 }}>{pct1(value)}</LTR>
      </div>
      <div style={{ height: 6, borderRadius: T.radius.pill, background: UI.line.hairline, marginTop: 5, overflow: "hidden" }}>
        <div style={{ width: `${Math.max(value > 0 ? 1 : 0, Math.min(100, value))}%`, height: "100%", borderRadius: T.radius.pill, background: muted ? UI.series[3] : UI.series[1] }}/>
      </div>
      {sub && <div style={{ ...TYPE.caption, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Fact({ children }) {
  return <div style={{ fontSize: 14.5, color: UI.text.primary, lineHeight: 1.75 }}>{children}</div>;
}

// ── 1 · Concentration ────────────────────────────────────────────────────────
function ConcentrationSection({ conc, asOf, total, cur }) {
  const [open, setOpen] = useState(false);
  const shown = open ? conc.rows : conc.rows.slice(0, 5);
  const restCount = conc.rows.length - 5;
  const restPct = conc.rows.slice(5).reduce((a, r) => a + r.weightPct, 0);
  const top = conc.rows[0];
  return (
    <Section n="1" title="ריכוזיות"
      basis={<>כל אחוז הוא מתוך שווי התיק · <Money value={total} cur={cur} size={13} weight={600} color={UI.text.secondary}/></>}
      source={<>מקור: מחיר אחרון מ-<LTR>Finnhub</LTR> × הכמות שבתיק{asOf ? <> · מחירים נכון ל-<LTR className="si-num">{clock(asOf)}</LTR></> : null}</>}>
      <div style={{ marginBottom: T.scale[2] }}>
        <Fact><LTR style={{ fontWeight: 800 }}>{top.symbol}</LTR> היא ההחזקה הגדולה ביותר · <LTR className="si-num" style={{ fontWeight: 700 }}>{pct1(top.weightPct)}</LTR> מהתיק</Fact>
        {conc.count > 5 ? (
          <Fact><LTR className="si-num">5</LTR> ההחזקות הגדולות · <LTR className="si-num" style={{ fontWeight: 700 }}>{pct1(conc.topPct)}</LTR> מהתיק</Fact>
        ) : conc.count > 1 ? (
          <Fact>בתיק <LTR className="si-num">{conc.count}</LTR> החזקות</Fact>
        ) : null}
      </div>
      {shown.map(r => <BarRow key={r.symbol} label={<LTR>{r.symbol}</LTR>} value={r.weightPct}/>)}
      {restCount > 0 && (
        <button onClick={() => setOpen(o => !o)} style={LINK_BTN}>
          {open ? <>הצג רק את <LTR className="si-num">5</LTR> הגדולות</> : <>עוד <LTR className="si-num">{restCount}</LTR> החזקות · <LTR className="si-num">{pct1(restPct)}</LTR> מהתיק</>}
        </button>
      )}
    </Section>
  );
}

// ── 2 · Sectors · the one ring on the screen ─────────────────────────────────
function Ring({ rows, tones }) {
  const R = 52, C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <svg width="136" height="136" viewBox="0 0 136 136" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="68" cy="68" r={R} fill="none" stroke={UI.line.hairline} strokeWidth="16"/>
      {rows.map(r => {
        const len = (r.weightPct / 100) * C;
        const el = (
          <circle key={r.sector} cx="68" cy="68" r={R} fill="none"
            stroke={tones[r.sector]} strokeWidth="16"
            strokeDasharray={`${Math.max(0, len - 1.5)} ${C}`} strokeDashoffset={-acc}
            transform="rotate(-90 68 68)"/>
        );
        acc += len;
        return el;
      })}
    </svg>
  );
}

// DESIGN.md: categories walk the one ink→cream ramp, no extra hues. The
// categorical tokens are close navy steps of that ramp, and two adjacent
// slices of the ring were near-indistinguishable in both themes (phone check,
// 25.09). So the ring takes the ramp's four widest steps, in an order that
// never puts two neighbouring steps side by side: dark, light, mid, palest.
// The two lines that are not sectors stay neutral: an unresolved holding must
// not read as an identity.
const RING_STEPS = [UI.series[0], UI.series[2], UI.series[1], UI.series[3], UI.series[4]];
const toneMap = rows => {
  const m = {};
  let i = 0;
  for (const r of rows) {
    if (r.sector === SECTOR_PENDING || r.sector === SECTOR_UNKNOWN) m[r.sector] = UI.text.muted;
    else m[r.sector] = RING_STEPS[i++ % RING_STEPS.length];
  }
  // The ring closes on itself: with a sixth sector the last slice would wear
  // the first slice's step, right next to it. Give it a step neither
  // neighbour has.
  const named = rows.filter(r => r.sector !== SECTOR_PENDING && r.sector !== SECTOR_UNKNOWN);
  if (named.length > RING_STEPS.length && named.length % RING_STEPS.length === 1) {
    m[named[named.length - 1].sector] = RING_STEPS[2];
  }
  return m;
};

function SectorSection({ rows }) {
  const tones = toneMap(rows);
  return (
    <Section n="2" title="סקטורים"
      basis="חלוקת שווי התיק לפי הסקטור של כל החזקה"
      source={<>מקור: הענף של כל חברה ב-<LTR>Finnhub</LTR>, ממופה לסקטורים של סוריקטה · נבדק מחדש כל 30 יום</>}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: T.space.md, flexWrap: "wrap" }}>
        <Ring rows={rows} tones={tones}/>
        <div style={{ flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map(r => (
            <div key={r.sector} style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: tones[r.sector], flexShrink: 0, alignSelf: "center" }}/>
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: UI.text.primary }}>
                {r.sector}
                <LTR style={{ display: "block", fontSize: 12, fontWeight: 500, color: UI.text.muted }}>{r.symbols.join(" · ")}</LTR>
              </span>
              <LTR className="si-num" style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary }}>{pct1(r.weightPct)}</LTR>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ── 3 · Currency ─────────────────────────────────────────────────────────────
// One line, not a bar: every priced holding is quoted in dollars (the only
// prices the app can fetch are US listings), so a bar at 100% says nothing a
// sentence does not. The shekel value is the same total at the live rate,
// with the rate, its source and its hour.
function CurrencySection({ total, asOf }) {
  const ils = useFxRates().ILS;
  return (
    <Section n="3" title="מטבע"
      source={<>מקור: ציטוטי <LTR>Finnhub</LTR> מהבורסות בארה"ב{asOf ? <> · נכון ל-<LTR className="si-num">{clock(asOf)}</LTR></> : null}</>}>
      <div style={{ fontSize: 14.5, color: UI.text.primary, lineHeight: 1.75 }}>
        כל התיק נקוב בדולר
        {ils?.rate > 0 && (
          <> · שווי בשקלים <Amount value={total * ils.rate} currency="ILS" size={null} weight={700}/>
          {" "}לפי שער <Amount value={ils.rate} currency="ILS" decimals={3} size={null} weight={null}/>
          {asOfNote(ils.at, ils.date) ? <>, {asOfNote(ils.at, ils.date)}</> : null}</>
        )}
      </div>
      {ils?.source && <div style={{ ...TYPE.caption, marginTop: 2 }}>השער: {ils.source}</div>}
    </Section>
  );
}

// ── 4 · Revenue by region ────────────────────────────────────────────────────
// `other` holds real places without a bucket of their own (Middle East,
// Africa) and any filing label the map does not know · the labels are named
// under the list when there are any.
const PLACE_HE = { ...REGION_HE, other: "אזורים אחרים" };

function RegionSection({ reg, src, loading }) {
  const [showEmpty, setShowEmpty] = useState(false);
  if (loading) {
    return (
      <Section n="4" title="הכנסות לפי אזור">
        <Loading>{[1, 2, 3, 4].map(i => <Skeleton key={i} h={30} mb={8}/>)}</Loading>
      </Section>
    );
  }
  // Exactly zero is "no reported revenue"; anything above it is a real line,
  // however small, so the rows still add to 100.
  const withRev = reg.places.filter(p => p.pct > 0);
  const empty = reg.places.filter(p => p.pct <= 0);
  const years = reg.years;
  const yearText = years.length === 0 ? null : years.length === 1 ? String(years[0]) : `${years[0]}–${years[years.length - 1]}`;
  return (
    <Section n="4" title="הכנסות לפי אזור"
      basis={<>לפי דיווחי החברות ל-SEC{yearText ? <> · שנת דיווח <LTR className="si-num">{yearText}</LTR></> : null} · משוקלל לפי שווי ההחזקה · מתוך שווי התיק</>}
      source={<>מקור: <LTR>SEC EDGAR</LTR> · הדוח השנתי{src?.filed ? <> · ההגשה האחרונה שנכללה <LTR className="si-num">{dateNumeric(src.filed)}</LTR></> : null}</>}>
      {withRev.length === 0 ? (
        <EmptyState icon="globe" line="לאף אחת מההחזקות אין פילוח הכנסות לפי אזור במערכת"/>
      ) : withRev.map(p => <BarRow key={p.id} label={PLACE_HE[p.id]} value={p.pct} sub={definitionNotes(reg, p.id)}/>)}

      {definedBy(reg).length > 0 && (
        <div style={{ ...TYPE.caption, marginTop: 4 }}>
          הגדרות האזורים לפי דוחות החברות: {definedBy(reg).map((d, i) => (
            <span key={d.symbol}>{i > 0 && " · "}<LTR>{d.symbol}</LTR> בדוח <LTR>{d.form}</LTR> לשנת <LTR className="si-num">{d.year}</LTR></span>
          ))}
        </div>
      )}

      {reg.notes?.unknownLabels?.length > 0 && withRev.some(p => p.id === "other") && (
        <div style={{ ...TYPE.caption, marginTop: 4 }}>
          ב"אזורים אחרים" נכללות גם תוויות שאינן מזוהות: <LTR>{reg.notes.unknownLabels.join(" · ")}</LTR>
        </div>
      )}

      {reg.notBrokenOutPct > 0 && (
        <div style={{ marginTop: T.scale[2], paddingTop: T.scale[2], borderTop: `1px dashed ${UI.line.hairline}` }}>
          <BarRow muted label="לא פורט לאזור" value={reg.notBrokenOutPct}
            sub={<>
              {reg.unspecifiedRevenuePct > 0 && <>הכנסות שהחברות לא שייכו לאזור · <LTR className="si-num">{pct1(reg.unspecifiedRevenuePct)}</LTR></>}
              {reg.unspecifiedRevenuePct > 0 && reg.noBreakdownPct > 0 && " · "}
              {reg.noBreakdownPct > 0 && <>החזקות בלי פילוח אזורי · <LTR className="si-num">{pct1(reg.noBreakdownPct)}</LTR></>}
              {" · הפירוט במקטע "}<LTR className="si-num">5</LTR>
            </>}/>
        </div>
      )}

      {empty.length > 0 && (
        <>
          <button onClick={() => setShowEmpty(v => !v)} style={LINK_BTN} aria-expanded={showEmpty}>
            {showEmpty ? "הסתר" : <>אזורים בלי הכנסות מדווחות · <LTR className="si-num">{empty.length}</LTR></>}
          </button>
          {showEmpty && (
            <div style={{ ...TYPE.bodySmall, fontSize: 13.5 }}>
              {empty.map(p => PLACE_HE[p.id]).join(" · ")}
              <div style={{ ...TYPE.caption }}>אף אחת מההחזקות לא דיווחה הכנסות שמשויכות לאזורים אלה.</div>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

// For a holding whose own region names we hold, every one of its regions that
// lands in this row is named · with the company's definition where it has
// one, and its share of the company's revenue. Listing only the regions that
// carried a note left Japan out of Apple's Asia-Pacific row, although it was
// counted in the number (phone check, 25.09).
function definitionNotes(reg, regionId) {
  const out = [];
  for (const h of reg.perHolding || []) {
    const def = companyRegionDef(h.symbol, h.year);
    if (!def) continue;
    const parts = (h.labels || [])
      .filter(l => def.labels[l] && classifyRegionLabel(l).region === regionId)
      .map(l => ({ l, d: def.labels[l], share: (h.labelShares || {})[l] }))
      .sort((a, b) => (b.share || 0) - (a.share || 0));
    if (!parts.length) continue;
    out.push(<span key={h.symbol + regionId} style={{ display: "block" }}>
      <LTR>{h.symbol}</LTR>, לפי הגדרת החברה: {parts.map((p, i) => (
        <span key={p.l}>{i > 0 && " · "}"{p.d.he}"{p.share != null && <> <LTR className="si-num">{(p.share * 100).toFixed(1)}%</LTR></>}{p.d.note && <> ({p.d.note})</>}</span>
      ))}
    </span>);
  }
  return out.length ? out : null;
}

// Which holdings' definitions were used, with the filing they come from.
function definedBy(reg) {
  return (reg.perHolding || []).flatMap(h => {
    const def = companyRegionDef(h.symbol, h.year);
    return def ? [{ symbol: h.symbol, ...def.source }] : [];
  });
}

// ── 5 · What we do not know yet ──────────────────────────────────────────────
function GapsSection({ list, loading }) {
  return (
    <Section n="5" title="מה אנחנו עדיין לא יודעים"
      basis="לכל החזקה · איזה מידע חסר במסכים שלמעלה"
      source="פער במידע, לא הערכה של ההחזקה">
      {loading ? (
        <Loading>{[1, 2].map(i => <Skeleton key={i} h={30} mb={8}/>)}</Loading>
      ) : list.length === 0 ? (
        <div style={{ ...TYPE.bodySmall }}>לכל ההחזקות יש ציטוט מחיר, סקטור ופילוח הכנסות לפי אזור.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {/* No rule under the last row · the source line's own rule follows,
              and the two stacked read as a double line (phone check, 25.09). */}
          {list.map((g, i) => (
            <div key={g.symbol} style={{ padding: `${T.scale[2]}px 0`, borderBottom: i < list.length - 1 ? `1px solid ${UI.line.hairline}` : "none", fontSize: 14, lineHeight: 1.7, color: UI.text.secondary }}>
              <LTR style={{ fontWeight: 800, color: UI.text.primary }}>{g.symbol}</LTR>
              {g.weightPct != null && <> · <LTR className="si-num">{pct1(g.weightPct)}</LTR> מהתיק</>}
              {g.items.map((it, i) => (
                <span key={i}> · {typeof it === "string" ? it : <><LTR className="si-num">{pct1(it.unspecified)}</LTR> מהכנסותיה לא שויכו לאזור בדיווח</>}</span>
              ))}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

const LINK_BTN = {
  background: "transparent", border: "none", padding: "10px 0 2px", minHeight: 44,
  fontSize: 13.5, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit",
  display: "inline-flex", alignItems: "center", gap: 4,
};

export default function ExposureTab({ portfolio = [], cur = "USD" }) {
  const isDesktop = useIsDesktop();
  const base = useMemo(() => valuedHoldings(portfolio), [portfolio]);
  const symbols = base.rows.map(r => r.symbol);
  const data = usePortfolioSegments(symbols);

  const conc = useMemo(() => concentration(base), [base]);
  const sect = useMemo(() => sectors(base), [base]);
  const reg  = useMemo(() => (data ? regions(base, data.segmentsBySymbol) : null), [base, data]);
  const gapList = useMemo(() => gaps(base, reg), [base, reg]);
  const src = data ? exposureSource(data.segmentsBySymbol) : null;
  // The OLDEST quote time: "prices as of" must hold for every price in the sum.
  const stamps = base.rows.map(r => r.quoteAt).filter(Boolean);
  const asOf = stamps.length ? Math.min(...stamps) : null;

  const head = (
    <div style={isDesktop ? { gridColumn: "1 / -1" } : null}>
      <div style={TYPE.h1}>חשיפות התיק</div>
      <div style={{ ...TYPE.bodySmall, marginTop: 4 }}>מה נמצא מאחורי ההחזקות שלך · חמש חלוקות של אותו שווי תיק.</div>
    </div>
  );

  if (!base.rows.length) {
    return (
      <div dir="rtl" style={{ display: "flex", flexDirection: "column", gap: T.space.md, maxWidth: 680, margin: "0 auto" }}>
        {head}
        <Card>
          <EmptyState icon="globe"
            line={portfolio.length ? "אין עדיין מחיר להחזקות שבתיק" : "אין החזקות בתיק"}
            sub={portfolio.length ? "החלוקות מחושבות משווי ההחזקות, והוא מחכה לציטוט." : "הוספת החזקה נעשית במסך התיק."}/>
        </Card>
        {!isDesktop && <div style={{ ...TYPE.caption, textAlign: "center" }}>מידע בלבד · אינו ייעוץ השקעות</div>}
      </div>
    );
  }

  const s1 = <ConcentrationSection conc={conc} asOf={asOf} total={base.total} cur={cur}/>;
  const s2 = <SectorSection rows={sect}/>;
  const s3 = <CurrencySection total={base.total} asOf={asOf}/>;
  const s4 = <RegionSection reg={reg} src={src} loading={!reg}/>;
  const s5 = <GapsSection list={gapList} loading={!reg}/>;

  // Phone: one column in the order the questions are asked. Desktop: a main
  // area (the holdings, where the revenue comes from, what is missing) and a
  // side area (sectors, currency) · not a grid of equal boxes.
  if (!isDesktop) {
    return (
      <div dir="rtl" style={{ display: "flex", flexDirection: "column", gap: T.space.md, maxWidth: 680, margin: "0 auto" }}>
        {head}{s1}{s2}{s3}{s4}{s5}
        {/* On the phone this screen is an overlay over the fixed bar that
            carries the disclaimer (App · MOBILE_OVERLAYS), so it says it
            itself. On a desktop the bar is in view (sprint 17). */}
        <div style={{ ...TYPE.caption, textAlign: "center" }}>מידע בלבד · אינו ייעוץ השקעות</div>
      </div>
    );
  }
  return (
    <div dir="rtl" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.45fr) minmax(0, 1fr)", columnGap: 24, rowGap: T.space.md, alignItems: "start", maxWidth: 1120, margin: "0 auto" }}>
      {head}
      <div style={{ display: "flex", flexDirection: "column", gap: T.space.md }}>{s1}{s4}{s5}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: T.space.md }}>{s2}{s3}</div>
    </div>
  );
}

